import React, { useEffect, useRef, useState } from 'react'

import useAffirmConfig from '~/hooks/affirm-config/affirm-config'

import { Wrapper } from './affirm-marketing.styles'

import type { TextAlign } from '~/styles/theme'

type Props = {
  cartTotal: number
  hideLearnMore?: boolean
  textAlign?: TextAlign
}

const AffirmMarketing = ({
  cartTotal,
  hideLearnMore = false,
  textAlign = 'left',
}: Props) => {
  const { loaded, validForAffirm, marketingDataPromoId } = useAffirmConfig()
  const [affirmWrapperRef, setAffirmWrapperRef] = useState<HTMLElement>()
  const affirmRefreshRequestContext = useRef({
    queued: false,
    awaitingResponse: false,
  })
  // Maintain a separate state for loadingMarketingContent rather than use affirmRefreshRequestContext
  // to avoid unnecessary useEffect executions while allowing the triggering of a re-render
  const [loadingMarketingContent, setLoadingMarketingContent] = useState(false)
  const mutationObserverRef = useRef<MutationObserver>(
    new MutationObserver(() => {
      if (affirmRefreshRequestContext.current.queued) {
        affirmRefreshRequestContext.current.queued = false
        affirmRefreshRequestContext.current.awaitingResponse = true
        setLoadingMarketingContent(true)
        window.affirm.ui.refresh()
      } else {
        affirmRefreshRequestContext.current.awaitingResponse = false
        setLoadingMarketingContent(false)
      }
    }),
  )

  useEffect(() => {
    if (affirmWrapperRef) {
      mutationObserverRef.current.observe(affirmWrapperRef, {
        childList: true,
      })

      return () => {
        mutationObserverRef.current.disconnect()
      }
    }
  }, [affirmWrapperRef])

  useEffect(() => {
    const { affirm } = window

    if (loaded && affirm && affirmWrapperRef) {
      affirm.ui.ready(() => {
        if (affirmRefreshRequestContext.current.awaitingResponse) {
          affirmRefreshRequestContext.current.queued = true
        } else {
          affirmRefreshRequestContext.current.awaitingResponse = true
          affirm.ui.refresh()
          setLoadingMarketingContent(true)
        }
      })
    }
  }, [loaded, affirmWrapperRef, cartTotal, marketingDataPromoId])

  if (!validForAffirm) return null

  return (
    <Wrapper
      $loading={loadingMarketingContent}
      ref={setAffirmWrapperRef}
      className="affirm-as-low-as"
      $textAlign={textAlign}
      data-page-type="cart"
      data-amount={cartTotal}
      data-learnmore-show={!hideLearnMore}
      data-testid="affirm-marketing"
      data-promo-id={marketingDataPromoId}
    />
  )
}

export default AffirmMarketing
